import { useState, useCallback, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { formFromEvent } from './eventFormMapping';
import { getStatusBadge, getStatusLabel, formatDate, formatShortDate } from './eventHelpers';
import {
  EventListItem, EventDetail, HostOption, EventForm, SponsorForm, WaypointForm,
  EMPTY_FORM, EMPTY_SPONSOR, EMPTY_WAYPOINT,
} from './eventTypes';

export function useEventManagement() {
  const [events, setEvents] = useState<EventListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState<EventDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Filters
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Create/Edit form
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState<string | null>(null);
  const [form, setForm] = useState<EventForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formStep, setFormStep] = useState(0); // 0=basics, 1=location, 2=ride settings, 3=advanced, 4=sponsors/waypoints

  // Host search
  const [hostSearch, setHostSearch] = useState('');
  const [hostOptions, setHostOptions] = useState<HostOption[]>([]);
  const [hostSearching, setHostSearching] = useState(false);
  const [selectedHostName, setSelectedHostName] = useState('');

  // Sponsor/waypoint forms
  const [sponsorForm, setSponsorForm] = useState<SponsorForm>(EMPTY_SPONSOR);
  const [waypointForm, setWaypointForm] = useState<WaypointForm>(EMPTY_WAYPOINT);
  const [showSponsorForm, setShowSponsorForm] = useState(false);
  const [showWaypointForm, setShowWaypointForm] = useState(false);

  // Cancel/delete modals
  const [cancelConfirm, setCancelConfirm] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // Notifications
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showNotification = useCallback((message: string, type: 'success' | 'error') => {
    setNotification({ message, type });
    const timeout = type === 'error' ? 30000 : 5000;
    setTimeout(() => setNotification(null), timeout);
  }, []);

  // ── Data fetching ──────────────────────────────────────────

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { p_limit: 500 };
      if (filterStatus !== 'all') params.p_status = filterStatus;
      if (filterType !== 'all') params.p_event_type = filterType;
      if (searchQuery.trim()) params.p_search = searchQuery.trim();

      const { data, error } = await supabase.rpc('admin_list_events', params);
      if (error) throw error;
      setEvents(Array.isArray(data) ? data : []);
    } catch (err: any) {
      showNotification(`Failed to load events: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterType, searchQuery, showNotification]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const fetchEventDetail = useCallback(async (eventId: string) => {
    setDetailLoading(true);
    try {
      const { data, error } = await supabase.rpc('admin_get_event_detail', { p_event_id: eventId });
      if (error) throw error;
      setSelectedEvent(data as EventDetail);
    } catch (err: any) {
      showNotification(`Failed to load event details: ${err.message}`, 'error');
    } finally {
      setDetailLoading(false);
    }
  }, [showNotification]);

  // Host search
  const searchHosts = useCallback(async (query: string) => {
    if (!query.trim()) { setHostOptions([]); return; }
    setHostSearching(true);
    try {
      const { data, error } = await supabase.rpc('admin_get_users_for_host_selection', { p_search: query });
      if (error) throw error;
      setHostOptions(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error('Host search error:', err);
    } finally {
      setHostSearching(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => searchHosts(hostSearch), 300);
    return () => clearTimeout(timer);
  }, [hostSearch, searchHosts]);

  // ── Form helpers ───────────────────────────────────────────

  const openCreateForm = () => {
    setForm(EMPTY_FORM);
    setEditingEvent(null);
    setFormStep(0);
    setSelectedHostName('');
    setHostSearch('');
    setShowForm(true);
  };

  const openEditForm = (event: EventDetail) => {
    setForm(formFromEvent(event));
    setEditingEvent(event.id);
    setSelectedHostName(event.host_name || event.host_email || '');
    setFormStep(0);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      showNotification('Title is required', 'error');
      return;
    }
    if (!form.meetup_time) {
      showNotification('Meetup time is required', 'error');
      return;
    }
    if (!editingEvent && !form.host_id) {
      showNotification('Please select a host', 'error');
      return;
    }

    setSaving(true);
    try {
      if (editingEvent) {
        const params: any = { p_event_id: editingEvent };
        if (form.title) params.p_title = form.title;
        if (form.description) params.p_description = form.description;
        if (form.event_type) params.p_event_type = form.event_type;
        if (form.privacy_level) params.p_privacy_level = form.privacy_level;
        if (form.meetup_time) params.p_meetup_time = new Date(form.meetup_time).toISOString();
        if (form.kick_stands_up_time) params.p_kick_stands_up_time = new Date(form.kick_stands_up_time).toISOString();
        if (form.location_name) params.p_location_name = form.location_name;
        if (form.address) params.p_address = form.address;
        if (form.city) params.p_city = form.city;
        if (form.state) params.p_state = form.state;
        if (form.latitude) params.p_latitude = parseFloat(form.latitude);
        if (form.longitude) params.p_longitude = parseFloat(form.longitude);
        if (form.max_participants) params.p_max_participants = parseInt(form.max_participants);
        if (form.skill_level) params.p_skill_level = form.skill_level;
        if (form.riding_style) params.p_riding_style = form.riding_style;
        if (form.riding_style_min) params.p_riding_style_min = form.riding_style_min;
        if (form.riding_style_max) params.p_riding_style_max = form.riding_style_max;
        if (form.route_type) params.p_route_type = form.route_type;
        if (form.lane_filtering) params.p_lane_filtering = form.lane_filtering;
        if (form.host_notes) params.p_host_notes = form.host_notes;
        if (form.participant_rules) params.p_participant_rules = form.participant_rules;
        if (form.gps_start_latitude) params.p_gps_start_latitude = parseFloat(form.gps_start_latitude);
        if (form.gps_start_longitude) params.p_gps_start_longitude = parseFloat(form.gps_start_longitude);
        if (form.gps_end_latitude) params.p_gps_end_latitude = parseFloat(form.gps_end_latitude);
        if (form.gps_end_longitude) params.p_gps_end_longitude = parseFloat(form.gps_end_longitude);
        params.p_enable_route_sharing = form.enable_route_sharing;
        params.p_chat_locked = form.chat_locked;
        if (form.status) params.p_status = form.status;
        if (form.allowed_gender) params.p_allowed_gender = form.allowed_gender;
        const motoRestrictions: any = {};
        if (form.motorcycle_restrictions.makes.length > 0) motoRestrictions.makes = form.motorcycle_restrictions.makes;
        if (form.motorcycle_restrictions.yearMin) motoRestrictions.yearMin = parseInt(form.motorcycle_restrictions.yearMin);
        if (form.motorcycle_restrictions.yearMax) motoRestrictions.yearMax = parseInt(form.motorcycle_restrictions.yearMax);
        if (form.motorcycle_restrictions.displacementMin) motoRestrictions.displacementMin = parseInt(form.motorcycle_restrictions.displacementMin);
        if (form.motorcycle_restrictions.displacementMax) motoRestrictions.displacementMax = parseInt(form.motorcycle_restrictions.displacementMax);
        if (Object.keys(motoRestrictions).length > 0) params.p_motorcycle_restrictions = motoRestrictions;

        const { data, error } = await supabase.rpc('admin_update_event', params);
        if (error) throw error;
        if (data && !data.success) throw new Error(data.error || 'Update failed');
        showNotification('Event updated', 'success');
      } else {
        const params: any = {
          p_host_id: form.host_id,
          p_title: form.title,
          p_event_type: form.event_type,
          p_privacy_level: form.privacy_level,
          p_meetup_time: new Date(form.meetup_time).toISOString(),
          p_status: form.status,
          p_latitude: form.latitude ? parseFloat(form.latitude) : 0,
          p_longitude: form.longitude ? parseFloat(form.longitude) : 0,
          p_enable_route_sharing: form.enable_route_sharing,
          p_chat_locked: form.chat_locked,
          p_chat_room_start_time: form.chat_room_start_time,
        };
        if (form.description) params.p_description = form.description;
        if (form.kick_stands_up_time) params.p_kick_stands_up_time = new Date(form.kick_stands_up_time).toISOString();
        if (form.location_name) params.p_location_name = form.location_name;
        if (form.address) params.p_address = form.address;
        if (form.city) params.p_city = form.city;
        if (form.state) params.p_state = form.state;
        if (form.max_participants) params.p_max_participants = parseInt(form.max_participants);
        if (form.skill_level) params.p_skill_level = form.skill_level;
        if (form.riding_style) params.p_riding_style = form.riding_style;
        if (form.riding_style_min) params.p_riding_style_min = form.riding_style_min;
        if (form.riding_style_max) params.p_riding_style_max = form.riding_style_max;
        if (form.route_type) params.p_route_type = form.route_type;
        if (form.lane_filtering) params.p_lane_filtering = form.lane_filtering;
        if (form.host_notes) params.p_host_notes = form.host_notes;
        if (form.participant_rules) params.p_participant_rules = form.participant_rules;
        if (form.gps_start_latitude) params.p_gps_start_latitude = parseFloat(form.gps_start_latitude);
        if (form.gps_start_longitude) params.p_gps_start_longitude = parseFloat(form.gps_start_longitude);
        if (form.gps_end_latitude) params.p_gps_end_latitude = parseFloat(form.gps_end_latitude);
        if (form.gps_end_longitude) params.p_gps_end_longitude = parseFloat(form.gps_end_longitude);
        if (form.allowed_gender) params.p_allowed_gender = form.allowed_gender;
        if (form.invite_code) params.p_invite_code = form.invite_code;
        const createMotoRestrictions: any = {};
        if (form.motorcycle_restrictions.makes.length > 0) createMotoRestrictions.makes = form.motorcycle_restrictions.makes;
        if (form.motorcycle_restrictions.yearMin) createMotoRestrictions.yearMin = parseInt(form.motorcycle_restrictions.yearMin);
        if (form.motorcycle_restrictions.yearMax) createMotoRestrictions.yearMax = parseInt(form.motorcycle_restrictions.yearMax);
        if (form.motorcycle_restrictions.displacementMin) createMotoRestrictions.displacementMin = parseInt(form.motorcycle_restrictions.displacementMin);
        if (form.motorcycle_restrictions.displacementMax) createMotoRestrictions.displacementMax = parseInt(form.motorcycle_restrictions.displacementMax);
        if (Object.keys(createMotoRestrictions).length > 0) params.p_motorcycle_restrictions = createMotoRestrictions;

        const { data, error } = await supabase.rpc('admin_create_event', params);
        if (error) throw error;
        if (data && !data.success) throw new Error(data.error || 'Create failed');
        showNotification('Event created', 'success');
      }
      setShowForm(false);
      setEditingEvent(null);
      setForm(EMPTY_FORM);
      await fetchEvents();
    } catch (err: any) {
      showNotification(`Failed to save event: ${err.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  // Cancel event
  const handleCancel = async () => {
    if (!cancelConfirm) return;
    try {
      const { data, error } = await supabase.rpc('admin_cancel_event', {
        p_event_id: cancelConfirm,
        p_reason: cancelReason || 'Cancelled by admin',
      });
      if (error) throw error;
      if (data && !data.success) throw new Error(data.error);
      showNotification('Event cancelled', 'success');
      setCancelConfirm(null);
      setCancelReason('');
      setSelectedEvent(null);
      await fetchEvents();
    } catch (err: any) {
      showNotification(`Failed to cancel: ${err.message}`, 'error');
    }
  };

  // Delete event
  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      const { data, error } = await supabase.rpc('admin_delete_event', { p_event_id: deleteConfirm });
      if (error) throw error;
      if (data && !data.success) throw new Error(data.error);
      showNotification('Event deleted', 'success');
      setDeleteConfirm(null);
      setSelectedEvent(null);
      await fetchEvents();
    } catch (err: any) {
      showNotification(`Failed to delete: ${err.message}`, 'error');
    }
  };

  // Add sponsor
  const handleAddSponsor = async () => {
    if (!selectedEvent || !sponsorForm.name) {
      showNotification('Sponsor name is required', 'error');
      return;
    }
    try {
      const { data, error } = await supabase.rpc('admin_add_event_sponsor', {
        p_event_id: selectedEvent.id,
        p_name: sponsorForm.name,
        p_website_url: sponsorForm.website_url || null,
        p_display_order: sponsorForm.display_order,
        p_tier: sponsorForm.tier,
        p_tagline: sponsorForm.tagline || null,
        p_fallback_color: sponsorForm.fallback_color,
      });
      if (error) throw error;
      if (data && !data.success) throw new Error(data.error);
      showNotification('Sponsor added', 'success');
      setSponsorForm(EMPTY_SPONSOR);
      setShowSponsorForm(false);
      await fetchEventDetail(selectedEvent.id);
    } catch (err: any) {
      showNotification(`Failed to add sponsor: ${err.message}`, 'error');
    }
  };

  // Remove sponsor
  const handleRemoveSponsor = async (sponsorId: string) => {
    if (!selectedEvent) return;
    try {
      const { error } = await supabase.rpc('admin_remove_event_sponsor', { p_sponsor_id: sponsorId });
      if (error) throw error;
      showNotification('Sponsor removed', 'success');
      await fetchEventDetail(selectedEvent.id);
    } catch (err: any) {
      showNotification(`Failed to remove sponsor: ${err.message}`, 'error');
    }
  };

  // Add waypoint
  const handleAddWaypoint = async () => {
    if (!selectedEvent || !waypointForm.name || !waypointForm.latitude || !waypointForm.longitude) {
      showNotification('Waypoint name and coordinates are required', 'error');
      return;
    }
    try {
      const { data, error } = await supabase.rpc('admin_add_event_waypoint', {
        p_event_id: selectedEvent.id,
        p_name: waypointForm.name,
        p_latitude: parseFloat(waypointForm.latitude),
        p_longitude: parseFloat(waypointForm.longitude),
        p_order_index: waypointForm.order_index,
        p_waypoint_type: waypointForm.waypoint_type,
        p_description: waypointForm.description || null,
      });
      if (error) throw error;
      if (data && !data.success) throw new Error(data.error);
      showNotification('Waypoint added', 'success');
      setWaypointForm({ ...EMPTY_WAYPOINT, order_index: (selectedEvent.waypoints?.length || 0) + 1 });
      setShowWaypointForm(false);
      await fetchEventDetail(selectedEvent.id);
    } catch (err: any) {
      showNotification(`Failed to add waypoint: ${err.message}`, 'error');
    }
  };

  // Remove waypoint
  const handleRemoveWaypoint = async (waypointId: string) => {
    if (!selectedEvent) return;
    try {
      const { error } = await supabase.rpc('admin_remove_event_waypoint', { p_waypoint_id: waypointId });
      if (error) throw error;
      showNotification('Waypoint removed', 'success');
      await fetchEventDetail(selectedEvent.id);
    } catch (err: any) {
      showNotification(`Failed to remove waypoint: ${err.message}`, 'error');
    }
  };

  // Stats
  const totalEvents = events.length;
  const scheduledCount = events.filter(e => e.status === 'scheduled').length;
  const activeCount = events.filter(e => e.status === 'in_progress').length;
  const completedCount = events.filter(e => e.status === 'completed').length;
  const cancelledCount = events.filter(e => e.status === 'cancelled').length;

  const inputClass = "w-full px-3 py-2 rounded-lg text-sm border border-white/10 focus:border-blue-400/50 focus:outline-none transition-colors";
  const inputStyle = { backgroundColor: 'var(--rp-surface-secondary)', color: 'var(--rp-text)' };

  return {
    events, loading, selectedEvent, setSelectedEvent, detailLoading,
    filterStatus, setFilterStatus, filterType, setFilterType, searchQuery, setSearchQuery,
    showForm, setShowForm, editingEvent, form, setForm, saving, formStep, setFormStep,
    hostSearch, setHostSearch, hostOptions, setHostOptions, hostSearching, selectedHostName, setSelectedHostName,
    sponsorForm, setSponsorForm, waypointForm, setWaypointForm,
    showSponsorForm, setShowSponsorForm, showWaypointForm, setShowWaypointForm,
    cancelConfirm, setCancelConfirm, cancelReason, setCancelReason,
    deleteConfirm, setDeleteConfirm, notification, setNotification,
    fetchEvents, fetchEventDetail, searchHosts, openCreateForm, openEditForm,
    handleSave, handleCancel, handleDelete, handleAddSponsor, handleRemoveSponsor,
    handleAddWaypoint, handleRemoveWaypoint, getStatusBadge, getStatusLabel,
    formatDate, formatShortDate, totalEvents, scheduledCount, activeCount,
    completedCount, cancelledCount, inputClass, inputStyle,
  };
}

export type EventModel = ReturnType<typeof useEventManagement>;
